/**
 * SgpRulesNote — Shows sportsbook-specific SGP rules when same-game legs are detected.
 * Compact one-line muted text below the legs list.
 */

import { SPORTSBOOKS } from '@config/constants';
import type { BuilderLeg } from '@parlay-picker/shared/builderLeg';
import { SPORTSBOOK_SGP_RULES } from '@parlay-picker/shared/correlationUtils';
import type { ReactElement } from 'react';

// =============================================================================
// Helpers
// =============================================================================

/** Human-readable labels for each SGP rule */
const RULE_LABELS: Record<keyof typeof SPORTSBOOK_SGP_RULES.draftkings, string> = {
  samePlayerDifferentProp: 'Same-player different props',
  playerPropPlusTeamProp: 'Player prop + team prop',
  playerPropPlusGameTotal: 'Player prop + game total',
  mlPlusGameTotal: 'ML + game total',
};

/** Check if there are 2+ legs sharing any gameId */
function hasSameGameLegs(legs: BuilderLeg[]): boolean {
  const gameIds = legs.map((l) => l.gameId).filter(Boolean);
  return new Set(gameIds).size < gameIds.length;
}

/** Get allowed SGP rules as human-readable strings */
function getAllowedRules(sportsbook: string): string[] {
  const rules = SPORTSBOOK_SGP_RULES[sportsbook.toLowerCase()];
  if (!rules) return [];

  const allowed: string[] = [];
  for (const [key, label] of Object.entries(RULE_LABELS)) {
    if (rules[key as keyof typeof rules]) {
      allowed.push(label);
    }
  }
  return allowed;
}

// =============================================================================
// Component
// =============================================================================

interface SgpRulesNoteProps {
  legs: BuilderLeg[];
  sportsbook: string | null;
}

export function SgpRulesNote({ legs, sportsbook }: SgpRulesNoteProps): ReactElement | null {
  if (!sportsbook || legs.length < 2 || !hasSameGameLegs(legs)) return null;

  const allowedRules = getAllowedRules(sportsbook);
  if (allowedRules.length === 0) return null;

  const bookName = SPORTSBOOKS[sportsbook]?.name ?? sportsbook;

  return (
    <div className="mb-2 px-2 text-[11px] text-theme-text-tertiary">
      {bookName} SGP: {allowedRules.join(', ')}
    </div>
  );
}
